import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Drawer } from 'antd'
import { useSelector } from 'react-redux'

import UserSpecialityTable, { DATA_TYPE_CHECK_CONTACT_PERSON, DATA_TYPE_SELECT } from './UserSpecialityTable'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { getSchoolSpecialities } from '../../../utils/api/school'
import { isObject } from '../../../utils'
import { getUserSpecialities, linkUserToSpeciality, unlinkUserToSpeciality } from '../../../utils/api/users'
import { onConsoleError, onError, onSuccess } from '../../../utils/apiHelper'
import { addContactPerson, addMultipleContactPersons } from '../../../utils/api/schoolSpeciality'
import { HttpStatuses } from '../../../utils/http'

const DRAWER_TITLE = 'Specialities supervised by'
const DEFAULT_TITLE = 'No user selected. Component desactivated'
const SUCCESSFUL_LINK_MESSAGE = 'Speciality successfully linked'
const SUCCESSFUL_UNLINK_MESSAGE = 'Speciality successfully unlinked'
const SUCCESSFUL_LINK_SWITCH_MESSAGE = 'Linked Speciality successfully modified'

const SupervisedUserSpecialities = ({ targetUser, onClose }) => {
  const { permissions, user } = useContext(GlobalContext)

  const t = useSelector(selectTranslate)

  const buildTitle = useMemo(() => targetUser?.email ? t(DRAWER_TITLE) + ' ' + targetUser.email : DEFAULT_TITLE, [targetUser])

  const handleClose = useCallback(() => {
    setUserSpecialities([])
    setContactPersonsSpecialities({})
    onClose()
  }, [onClose])

  const [availableSpecialities, setAvailableSpecialities] = useState([])
  const [userSpecialities, setUserSpecialities] = useState([])
  const [contactPersonsSpecialities, setContactPersonsSpecialities] = useState({})
  const [loading, setLoading] = useState(false)

  const userSpecialityYears = useMemo(() => [], [])

  useEffect(() => {
    if (user.school) {
      getSchoolSpecialities(user, user.school).then(({ data }) => {
        setAvailableSpecialities(data.map(s => {
          s.name = getSpecialityName(s.specialityYear)

          return s
        }))
      })
    }
  }, [user.school])

  useEffect(() => {
    if (isObject(targetUser) && user) {
      setLoading(true)
      getUserSpecialities(user, targetUser).then(({ data }) => {
        const userSpecialities = []
        const contactPersonsSpecialities = {}

        for (const speciality of data) {
          speciality.name = getSpecialityName(speciality.specialityYear)
          userSpecialities.push(speciality)

          contactPersonsSpecialities[speciality.id] = speciality.contactPersons.map(contactPerson => {
            return contactPerson.id
          })
        }

        setUserSpecialities(userSpecialities)
        setContactPersonsSpecialities(contactPersonsSpecialities)
        setLoading(false)
      })
    }
  }, [targetUser, user])

  const columns = useMemo(() => [
    {
      type: DATA_TYPE_SELECT,
      name: t('Speciality'),
      key: 'name',
      options: availableSpecialities.map((speciality, index) => {
        return {
          id: speciality.name,
          name: speciality.name,
          index
        }
      })
    },
    {
      type: DATA_TYPE_CHECK_CONTACT_PERSON,
      name: t('Contact persons'),
      key: 'contactPerson'
    }
  ], [t, availableSpecialities])

  const getSpecialityName = specialityYear => {
    if (specialityYear) {
      return `${specialityYear.specialityAbbreviation} (${specialityYear.studyYearName})`
    }

    return 'Unkown speciality'
  }

  const handleLinkSpeciality = ({ name }) => {
    const speciality = availableSpecialities.find(s => s.name === name)

    if (speciality) {
      linkUserToSpeciality(user, targetUser, speciality).then(({ data }) => {
        const newUserSpecialities = [...userSpecialities]
        const newContactPersonsSpecialities = { ...contactPersonsSpecialities }

        data.name = getSpecialityName(data.specialityYear)
        newUserSpecialities.push(data)

        newContactPersonsSpecialities[data.id] = data.contactPersons.map(contactPerson => {
          return contactPerson.id
        })

        setUserSpecialities(newUserSpecialities)
        setContactPersonsSpecialities(newContactPersonsSpecialities)
        onSuccess(t(SUCCESSFUL_LINK_MESSAGE))
      }).catch(specialityErrorHandler)
    }
  }

  const onModifyLinkSpeciality = (data) => {
    const speciality = availableSpecialities.find(s => s.name === data.name)
    const oldSpeciality = userSpecialities.find(s => s.id === data.id)

    if (speciality && oldSpeciality) {
      linkUserToSpeciality(user, targetUser, speciality).then(({ data }) => {
        const newUserSpecialities = [...userSpecialities]
        const newContactPersonsSpecialities = { ...contactPersonsSpecialities }

        data.name = getSpecialityName(data.specialityYear)
        newUserSpecialities.push(data)

        newContactPersonsSpecialities[data.id] = data.contactPersons.map(contactPerson => {
          return contactPerson.id
        })

        setContactPersonsSpecialities(newContactPersonsSpecialities)
        return newUserSpecialities
      }).then(userSpecialities => {
        unlinkUserToSpeciality(user, targetUser, oldSpeciality).then(({ data }) => {
          setUserSpecialities(userSpecialities.filter(sp => {
            return sp.id !== data.id
          }))

          const newContactPersonsSpecialities = {}

          for (const key in contactPersonsSpecialities) {
            if (parseInt(key) !== data.id) {
              newContactPersonsSpecialities[parseInt(key)] = contactPersonsSpecialities[parseInt(key)]
            }
          }

          setContactPersonsSpecialities(newContactPersonsSpecialities)
          onSuccess(t(SUCCESSFUL_LINK_SWITCH_MESSAGE))
        })
      }).catch(specialityErrorHandler)
    }
  }

  const onUnlinkSpeciality = ({ name }) => {
    const speciality = availableSpecialities.find(s => s.name === name)

    if (speciality) {
      unlinkUserToSpeciality(user, targetUser, speciality).then(({ data }) => {
        setUserSpecialities(userSpecialities.filter(sp => sp.id !== data.id))

        const newContactPersonsSpecialities = {}

        for (const key in contactPersonsSpecialities) {
          if (parseInt(key) !== data.id) {
            newContactPersonsSpecialities[parseInt(key)] = contactPersonsSpecialities[parseInt(key)]
          }
        }

        setContactPersonsSpecialities(newContactPersonsSpecialities)
        onSuccess(t(SUCCESSFUL_UNLINK_MESSAGE))
      }).catch(error => {
        onConsoleError(error, t('users.unlink_speciality.error'))
      })
    }
  }

  const specialityErrorHandler = error => {
    if (error.status === HttpStatuses.CONFLICT) {
      onError(t('users.link_speciality.error_already_linked'))
    } else {
      onConsoleError(error, t('users.link_speciality.error'))
    }
  }

  const userIsContactPerson = specialityId => {
    if (targetUser && contactPersonsSpecialities[specialityId]) {
      return contactPersonsSpecialities[specialityId].includes(targetUser.id)
    } else {
      return false
    }
  }

  const setMultipleUserContactPersons = schoolSpecialities => {
    const body = {
      userToAdd: targetUser.id,
      schoolSpecialities: schoolSpecialities
    }

    addMultipleContactPersons(user, body).then(({ data }) => {
      const newContactPersonsSpecialities = { ...contactPersonsSpecialities }

      for (const schoolSpecialityId of schoolSpecialities) {
        let contactPersonsSpeciality = []

        if (contactPersonsSpecialities[schoolSpecialityId].includes(targetUser.id)) {
          for (const userId of contactPersonsSpecialities[schoolSpecialityId]) {
            if (userId !== targetUser.id) {
              contactPersonsSpeciality.push(userId)
            }
          }
        } else {
          contactPersonsSpeciality = contactPersonsSpeciality.concat(contactPersonsSpecialities[schoolSpecialityId])
          contactPersonsSpeciality.push(targetUser.id)
        }

        newContactPersonsSpecialities[schoolSpecialityId] = contactPersonsSpeciality
      }

      setContactPersonsSpecialities(newContactPersonsSpecialities)
      onSuccess(t('Saved changes'))
    })
  }

  const setUserContactPerson = schoolSpecialityId => {
    const body = {
      userToAdd: targetUser.id
    }

    addContactPerson(user, { id: schoolSpecialityId }, body).then(({ data }) => {
      let contactPersonsSpeciality = []

      if (contactPersonsSpecialities[schoolSpecialityId].includes(targetUser.id)) {
        for (const userId of contactPersonsSpecialities[schoolSpecialityId]) {
          if (userId !== targetUser.id) {
            contactPersonsSpeciality.push(userId)
          }
        }
      } else {
        contactPersonsSpeciality = contactPersonsSpeciality.concat(contactPersonsSpecialities[schoolSpecialityId])
        contactPersonsSpeciality.push(targetUser.id)
      }

      const newContactPersonsSpecialities = { ...contactPersonsSpecialities }
      newContactPersonsSpecialities[schoolSpecialityId] = contactPersonsSpeciality

      setContactPersonsSpecialities(newContactPersonsSpecialities)
      onSuccess(t('Saved changes'))
    })
  }

  return (
    <Drawer
      title={buildTitle}
      width='640px'
      onClose={handleClose}
      visible={targetUser !== null}
    >
      {columns && (
        <UserSpecialityTable
          columns={columns}
          data={userSpecialities ?? userSpecialityYears}
          loading={loading}
          onDataAdd={handleLinkSpeciality}
          onDataEdit={onModifyLinkSpeciality}
          onDataDelete={onUnlinkSpeciality}
          addDataText={t('Add a speciality')}
          noDataText={t('This user does not supervise any speciality.')}
          selectAllText={t('Select all')}
          noDataSelected={t('Select a data')}
          deleteText={t('Delete')}
          titlePopConfirmText={t('Delete this speciality ?')}
          yesText={t('Yes')}
          cancelText={t('Cancel')}
          showAddButtonDisabled={!permissions.canAddSpeciality}
          userIsContactPerson={userIsContactPerson}
          setMultipleUserContactPersons={setMultipleUserContactPersons}
          setUserContactPerson={setUserContactPerson}
        />
      )}
    </Drawer>
  )
}

export default (SupervisedUserSpecialities)
